import { useEffect } from "react";

export interface SeoOptions {
  title: string;
  description: string;
  keywords?: string;
  path: string;
  jsonLd?: object[];
  image?: string;
}

export function getSiteUrl(path = "/"): string {
  const basePath = __BASE_PATH__.split("/").filter(Boolean).join("/");
  const pathPrefix = basePath ? `/${basePath}` : "";
  return `${window.location.origin}${pathPrefix}${path}`;
}

function upsertMeta(attr: "name" | "property", key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

const DEFAULT_OG_IMAGE =
  "https://readdy.ai/api/search-image?query=Epic%20anime%20key%20visual%20of%20a%20determined%20teenage%20boy%20with%20spiky%20dark%20hair%20standing%20amid%20roaring%20orange%20flames%2C%20a%20tiny%20baby%20hitman%20in%20a%20black%20suit%20on%20his%20shoulder%2C%20dramatic%20cinematic%20manga%20poster%20art%2C%20dark%20charcoal%20background%20filled%20with%20floating%20embers%20and%20sparks%2C%20intense%20warm%20orange%20rim%20lighting%2C%20high%20contrast%2C%20premium%20shonen%20manga%20promotional%20art&width=1600&height=900&seq=manga-hero-001&orientation=landscape";

export function useSeo({ title, description, keywords, path, jsonLd, image }: SeoOptions) {
  useEffect(() => {
    const url = getSiteUrl(path);
    const ogImage = image || DEFAULT_OG_IMAGE;

    document.title = title;
    upsertMeta("name", "description", description);
    if (keywords) upsertMeta("name", "keywords", keywords);
    upsertLink("canonical", url);
    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:type", "website");
    upsertMeta("property", "og:url", url);
    upsertMeta("property", "og:site_name", "土豪漫画网");
    upsertMeta("property", "og:locale", "zh_CN");
    upsertMeta("property", "og:image", ogImage);
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", ogImage);

    document.querySelectorAll('script[data-seo-jsonld]').forEach((el) => el.remove());
    if (jsonLd && jsonLd.length > 0) {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.dataset.seoJsonld = "true";
      script.textContent = JSON.stringify(jsonLd);
      document.head.appendChild(script);
    }
  }, [title, description, keywords, path, jsonLd, image]);
}